import { AnalyticsEventDef } from './analytics-events'
import { featuredGuestJourneyService } from './featured-guest-journey-service'
import { posthogService } from './posthog-service'
import { Hustler } from '../dao/core-dao'
import { attributionUtil } from '../util/attribution-util'
import { config } from '../util/config'
import { logger } from '../util/logger'
import { objectUtil } from '../util/object-util'

export type AnalyticsProperties = Record<string, unknown>

/**
 * An analytics call must never take the page down: analytics degrade, behaviour does not.
 * Swallow, log, move on.
 */
const attempt = (fn: () => void): void => {
  try {
    fn()
  } catch (error) {
    logger.debug('analytics failed', error)
  }
}

const ageBucket = (dateOfBirth: string | Date | null | undefined): string | undefined => {
  if (!dateOfBirth) return undefined
  const born = new Date(dateOfBirth)
  if (Number.isNaN(born.getTime())) return undefined
  const age = Math.floor((Date.now() - born.getTime()) / (365.25 * 24 * 60 * 60 * 1000))
  if (age <= 0) return undefined
  if (age < 25) return '18-24'
  if (age < 35) return '25-34'
  if (age < 45) return '35-44'
  if (age < 55) return '45-54'
  return '55+'
}

const anonymousSuperProperties = (): AnalyticsProperties => ({
  platform: 'web',
  app_env: config.env,
  auth_state: 'anonymous',
})

/**
 * The one door to product analytics. Call sites name the moment (`AnalyticsEvent.X`) and pass
 * what they know; this decides what reaches PostHog and how.
 *
 * Property keys go out in snake_case whatever the call site used, so the featured contract's
 * camelCase (shared with mobile) and the newer snake_case sites end up consistent.
 */
export const analyticsService = {
  /**
   * Boot, for guests and members alike. Idempotent: the app shell calls it once, the sign-in
   * page may call it again to be safe.
   */
  init: (): void => {
    attempt(() => {
      posthogService.init()
      posthogService.register(anonymousSuperProperties())
    })
  },

  /** Fire-and-forget by contract: never throws. */
  track: (event: AnalyticsEventDef, properties: AnalyticsProperties = {}): void => {
    attempt(() => posthogService.capture(event.name, objectUtil.snakeCaseKeysDeep(properties)))
  },

  /** Route changes. */
  pageview: (properties: AnalyticsProperties = {}): void => {
    attempt(() => posthogService.pageview(objectUtil.snakeCaseKeysDeep(properties)))
  },

  /**
   * Bind this browser's timeline to the account. `hustler.id` is the distinct id on every
   * surface (it is already RevenueCat's `app_user_id` and Stripe's `client_reference_id`),
   * which is what makes one person out of mobile, web and backend events.
   *
   * Also the moment the guest journey (GH-861) is stamped onto the account, once, then cleared
   * so the next account signed in on this browser does not inherit it.
   */
  identify: (me: Hustler): void => {
    const firstTouch = Object.fromEntries(
      Object.entries(attributionUtil.getAttributionParams()).map(([key, value]) => [`first_touch_${key}`, value]),
    )
    attempt(() => {
      posthogService.identify(me.id, {
        set: {
          email: me.email,
          username: me.profile?.username ?? undefined,
          gender: me.profile?.gender ?? undefined,
          age_bucket: ageBucket(me.profile?.dateOfBirth),
        },
        // A sign-up date and the campaign that brought them must never be rewritten later.
        setOnce: { ...(me.createdAt ? { signup_at: me.createdAt } : {}), ...firstTouch },
      })
      posthogService.register({ auth_state: 'authenticated' })
    })

    try {
      const guestProperties = featuredGuestJourneyService.toProperties()
      if (guestProperties && analyticsService.setProfileOnce(guestProperties)) {
        featuredGuestJourneyService.clear()
      }
    } catch (err) {
      logger.debug('featured guest journey not stamped', err)
    }
  },

  /**
   * Write person properties only where they are not already set. Returns whether the write
   * was attempted, so a journey that never landed can be kept for a later sign-in.
   */
  setProfileOnce: (properties: AnalyticsProperties): boolean => {
    let landed = false
    attempt(() => {
      landed = posthogService.setPersonProperties({ setOnce: objectUtil.snakeCaseKeysDeep(properties) })
    })
    return landed
  },

  /**
   * Sign-out. Without this the next account on the browser inherits the previous identity —
   * which is exactly what was happening before.
   */
  reset: (): void => {
    attempt(() => {
      posthogService.reset()
      posthogService.register(anonymousSuperProperties())
    })
  },
}
